/* eslint-disable max-lines */
import { Pagination, Tooltip } from '@heroui/react';
import { Settings as SettingsIcon } from 'lucide-react';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { ContentLoadError } from '@/components';
import { ds } from '@/config/colors';
import { useQueryHelper } from '@/queries';
import { useOrganizationState } from '@/state';
import { useLocaleNavigate } from '@/utils/locale';
import { PAGINATION_CLASS_NAMES } from '@/utils/paginationClassNames';
import { KdsDevicesSection } from '@/views/MyDevices/components';
import { useGetDevicesFromFirebase } from '@/views/MyDevices/queries';

import POD_BLACK from '../../../assets/images/black.png';
import POD_WHITE from '../../../assets/images/white.png';

/* ============================================================
   Kompaktowa tabela urządzeń — taki sam pattern jak reszta
   widoków list (kanały TV, oferty, hotel POI itp.).
   ============================================================ */

const ListTableWrap = styled.div`
  /* Tabela jak w hotelspot-audit: bez ramki, tła i bocznych wcięć (DS). */
  width: 100%;
  overflow-x: auto;
`;

const ListTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;

  /* Nagłówek wyraźny (13 px, medium, czarny tekst), linie nad i pod, bez szarego tła. */
  thead th {
    text-align: left;
    padding: 12px 16px;
    font-size: 13px;
    font-weight: 500;
    white-space: nowrap;
    color: ${({ theme }) => theme.colors.text.primary};
    background: transparent;
    border-top: 1px solid ${({ theme }) => (theme.mode === 'dark' ? theme.colors.border.primary : ds.borderStrong)};
    border-bottom: 1px solid ${({ theme }) => (theme.mode === 'dark' ? theme.colors.border.primary : ds.borderStrong)};
  }
  thead th:first-child,
  tbody td:first-child { padding-left: 0; }
  thead th:last-child,
  tbody td:last-child { padding-right: 0; }
  thead th.center { text-align: left; }
  thead th.right { text-align: left; }

  tbody tr {
    border-bottom: 1px solid ${({ theme }) => theme.colors.border.primary};
    transition: background 0.15s;
  }
  tbody tr:last-child { border-bottom: none; }
  tbody tr:hover {
    background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(17, 24, 39, 0.02)')};
  }

  tbody td {
    padding: 8px 16px;
    vertical-align: middle;
    color: ${({ theme }) => theme.colors.text.primary};
  }
  tbody td.center { text-align: center; }
  tbody td.right { text-align: right; }
  tbody td.muted { color: ${({ theme }) => theme.colors.text.secondary}; }
`;

const PreviewWrap = styled.div`
  position: relative;
  width: 44px;
  height: 44px;
  border-radius: 8px;
  background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : '#f3f4f6')};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  img {
    max-width: 80%;
    max-height: 80%;
    object-fit: contain;
  }
`;

const SizePill = styled.span`
  position: absolute;
  bottom: -4px;
  right: -4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 16px;
  padding: 0 5px;
  border-radius: 999px;
  font-size: 9.5px;
  font-weight: 400;
  background: #111827;
  color: #ffffff;
  border: 2px solid ${({ theme }) => (theme.mode === 'dark' ? '#1a1f26' : '#ffffff')};
`;

const DeviceName = styled.div`
  font-size: 13px;
  font-weight: 300;
  color: ${({ theme }) => theme.colors.text.primary};
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DeviceWelcome = styled.div`
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.text.secondary};
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ColorPill = styled.span<{ $color: 'white' | 'black' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 10px 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 300;
  text-transform: capitalize;
  background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : '#f3f4f6')};
  color: ${({ theme }) => theme.colors.text.primary};

  &::before {
    content: '';
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${({ $color }) => ($color === 'white' ? '#f3f4f6' : '#111827')};
    border: 1px solid ${({ $color }) => ($color === 'white' ? '#cbd5e1' : '#000000')};
  }
`;

const SizeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : '#f3f4f6')};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 11px;
  font-weight: 300;
`;

const CompactIconButton = styled.button<{ $variant?: 'secondary' | 'danger' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
  background: transparent;
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#dcdfe4')};
  color: ${({ $variant, theme }) => {
    if ($variant === 'danger') return '#dc2626';
    return theme.mode === 'dark' ? '#9ca3af' : '#6b7280';
  }};

  &:hover:not(:disabled) {
    background: ${({ $variant, theme }) => {
    if ($variant === 'danger') return theme.mode === 'dark' ? 'rgba(239,68,68,0.12)' : '#fee2e2';
    return theme.mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#f9fafb';
  }};
    border-color: ${({ $variant, theme }) => {
    if ($variant === 'danger') return '#fecaca';
    return theme.mode === 'dark' ? '#4b5563' : '#d1d5db';
  }};
  }

  svg { width: 14px; height: 14px; }
`;

const CompactActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  justify-content: flex-end;
`;

const EmptyTableContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  gap: 6px;
  text-align: center;
`;

const EmptyTitle = styled.p`
  margin: 0;
  font-size: 13px;
  font-weight: 300;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const EmptyDescription = styled.p`
  margin: 0;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  max-width: 380px;
  line-height: 1.5;
`;

const LoadingSkeleton = styled.div`
  height: 56px;
  width: 100%;
  border-radius: 12px;
  background: linear-gradient(
    90deg,
    rgba(0, 0, 0, 0.04) 0%,
    rgba(0, 0, 0, 0.07) 50%,
    rgba(0, 0, 0, 0.04) 100%
  );
  background-size: 200% 100%;
  animation: skeletonShimmer 1.6s ease-in-out infinite;

  @keyframes skeletonShimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`;

const PaginationWrap = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 12px 0 0;
`;

export const MyDevicesSection: FC = () => {
  const { t } = useTranslation();
  const navigate = useLocaleNavigate();

  const { organizationId, defaultLang } = useOrganizationState();
  const langContext = defaultLang;

  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = Number(searchParams.get('page')) || 1;

  const devicesQuery = useGetDevicesFromFirebase(organizationId || '', currentPage, 9);
  const { data: devices, refetch } = devicesQuery;
  const { previousData } = useQueryHelper({ queries: [devicesQuery] });

  const handleEdit = (e: React.MouseEvent, id: string): void => {
    e.stopPropagation();
    navigate(`/my-devices/${id}/edit`);
  };

  const isLoading = !!devices?.isLoading;
  const hasError = !!devices?.error;
  const items = devices?.data || [];
  const isEmpty = !isLoading && !hasError && items.length === 0;

  // Ekrany KDS mają własne źródło danych, więc pokazujemy je także wtedy, gdy
  // lista POD-ów się nie wczytała.
  if (hasError) {
    return (
      <>
        <ContentLoadError onRefreshClicked={() => { refetch(); }} />
        <KdsDevicesSection />
      </>
    );
  }

  if (isLoading) {
    return (
      <>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <LoadingSkeleton />
          <LoadingSkeleton />
          <LoadingSkeleton />
        </div>
        <KdsDevicesSection />
      </>
    );
  }

  return (
    <>
      <ListTableWrap>
        <ListTable>
          <thead>
            <tr>
              <th style={{ width: 60 }}>{t('myHotel.preview', 'Podgląd')}</th>
              <th>{t('myHotel.name', 'Nazwa')}</th>
              <th>{t('myHotel.welcomeText', 'Tekst powitalny')}</th>
              <th style={{ width: 90 }}>{t('myHotel.size', 'Rozmiar')}</th>
              <th style={{ width: 130 }}>{t('myHotel.color', 'Kolor')}</th>
              <th className="right" style={{ width: 80 }} />
            </tr>
          </thead>
          <tbody>
            {isEmpty ? (
              <tr>
                <td colSpan={6} style={{ padding: 0 }}>
                  <EmptyTableContent>
                    <EmptyTitle>
                      {t('myHotel.noDevicesRegistered', 'Brak zarejestrowanych urządzeń')}
                    </EmptyTitle>
                    <EmptyDescription>
                      {t(
                        'myHotel.noDevicesDescription',
                        'Po zarejestrowaniu pierwszego tabletu, telewizora lub kiosku zobaczysz go w tym miejscu.',
                      )}
                    </EmptyDescription>
                  </EmptyTableContent>
                </td>
              </tr>
            ) : (
              items.map((item) => {
                const color = (item.model?.color || 'black') as 'white' | 'black';
                return (
                  <tr key={item.id}>
                    <td>
                      <PreviewWrap>
                        <img
                          src={color === 'white' ? POD_WHITE : POD_BLACK}
                          alt={item.name}
                        />
                        <SizePill>
                          {item.model?.size}
                          &quot;
                        </SizePill>
                      </PreviewWrap>
                    </td>
                    <td>
                      <DeviceName>{item.name}</DeviceName>
                    </td>
                    <td>
                      <DeviceWelcome>
                        {item?.homeScreenWelcomeText?.[langContext] || '—'}
                      </DeviceWelcome>
                    </td>
                    <td>
                      <SizeBadge>
                        {item.model?.size}
                        &quot;
                      </SizeBadge>
                    </td>
                    <td>
                      <ColorPill $color={color}>
                        {color === 'white'
                          ? t('myHotel.colorWhite', 'Biały')
                          : t('myHotel.colorBlack', 'Czarny')}
                      </ColorPill>
                    </td>
                    <td className="right">
                      <CompactActions>
                        <Tooltip
                          content={t('myHotel.settings', 'Ustawienia')}
                          showArrow
                          radius="sm"
                          size="sm"
                          delay={300}
                          classNames={{
                            base: 'pointer-events-auto',
                            content: 'bg-white dark:bg-[#242a33] text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-[#374151] shadow-lg',
                          }}
                        >
                          <span>
                            <CompactIconButton
                              type="button"
                              $variant="secondary"
                              onClick={(e: React.MouseEvent) => handleEdit(e, item.id || '')}
                              aria-label={String(t('myHotel.settings', 'Ustawienia'))}
                            >
                              <SettingsIcon />
                            </CompactIconButton>
                          </span>
                        </Tooltip>
                      </CompactActions>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </ListTable>
      </ListTableWrap>

      {previousData && previousData?.totalPages > 1 && (
        <PaginationWrap>
          <Pagination
            showControls
            key="pag"
            size="sm"
            initialPage={currentPage}
            total={previousData?.totalPages}
            className="pagination-custom"
            classNames={PAGINATION_CLASS_NAMES}
            onChange={(page) => {
              setSearchParams(page === 1 ? {} : { page: page.toString() });
            }}
          />
        </PaginationWrap>
      )}

      <KdsDevicesSection />
    </>
  );
};
